import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useQuery } from 'react-query';
import Select from 'react-select';
import axios from 'axios';
import { toast } from 'react-toastify';
import CenteredSpinner from '../CenteredSpinner';

export const UpdateMarketStatusModal = ({
  showModal,
  setShowModal,
  propertyId,
  currentStatusCode,
  onSuccess,
}) => {
  const [marketStatusCode, setMarketStatusCode] = useState(null);
  const [marketStatusError, setMarketStatusError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchMarketStatusTokens = () => {
    const options = {
      method: 'GET',
      url: '/lookuptoken/getmarketstatustokens',
    };

    return axios
      .request(options)
      .then((response) => response.data ?? [])
      .catch(() => []);
  };

  const { data: marketStatusTokens, isLoading: isLoadingMarketStatus } = useQuery(
    ['marketStatusTokens'],
    fetchMarketStatusTokens,
    {
      enabled: Boolean(showModal),
      refetchOnWindowFocus: false,
    },
  );

  const marketStatusOptions = useMemo(() => {
    const hiddenStatusCodes = new Set(['PFA', 'DEL', 'DRFT']);
    return (marketStatusTokens || [])
      .filter((item) => !hiddenStatusCodes.has(item.stringValue))
      .map((item) => ({
        value: item.stringValue,
        label: (item.text || '').trim() || item.stringValue,
      }));
  }, [marketStatusTokens]);

  useEffect(() => {
    if (!showModal) return;
    setMarketStatusError(null);
    setMarketStatusCode(currentStatusCode ?? null);
  }, [showModal, currentStatusCode]);

  useEffect(() => {
    if (!showModal) return;
    if (!marketStatusOptions.length) return;
    if (marketStatusCode && !marketStatusOptions.some((option) => option.value === marketStatusCode)) {
      setMarketStatusCode(null);
    }
  }, [showModal, marketStatusOptions, marketStatusCode]);

  const selectedOption = useMemo(() => {
    return marketStatusOptions.find((option) => option.value === marketStatusCode) ?? null;
  }, [marketStatusOptions, marketStatusCode]);

  const handleMarketStatusChange = (option) => {
    setMarketStatusError(null);
    setMarketStatusCode(option ? option.value : null);
  };

  const handleUpdateMarketStatus = async () => {
    if (!propertyId) {
      setMarketStatusError('Property is required.');
      return;
    }
    if (!marketStatusCode) {
      setMarketStatusError('Market status is required.');
      return;
    }

    setIsSubmitting(true);
    const username = localStorage.getItem('username');

    const config = {
      method: 'POST',
      url: '/Property/UpdatePropertyMarketStatus',
      headers: {
        'Content-Type': 'application/json',
      },
      data: JSON.stringify({
        id: propertyId,
        marketStatusCode: marketStatusCode,
        UsernameSubmitted: username,
      }),
    };

    try {
      const data = await axios(config);

      if (data.status === 200 || data.status === 201) {
        toast.success(`Market status updated`, {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          draggable: true,
          progress: undefined,
        });
        setShowModal(false);
        if (onSuccess) onSuccess();
      } else {
        toast.error(`Sorry, market status wasn't updated, please try again.`, {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          draggable: true,
          progress: undefined,
        });
      }
    } catch (err) {
      toast.error(`Sorry, market status wasn't updated, try again ${err.message}`, {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        draggable: true,
        progress: undefined,
      });
    }

    setIsSubmitting(false);
  };

  return (
    <Modal show={showModal} backdrop="static" centered dialogClassName="property-modal">
      <Modal.Header
        closeButton
        onClick={() => {
          setMarketStatusError(null);
          setShowModal(false);
        }}
      >
        <Modal.Title>
          <span className="font-weight-bold">Update Market Status</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <label className="form-label mb-2 mt-2">Market status</label>
        {isLoadingMarketStatus ? (
          <CenteredSpinner size={30} />
        ) : (
          <Select
            classNamePrefix="react-select"
            value={selectedOption}
            onChange={handleMarketStatusChange}
            options={marketStatusOptions}
            isSearchable={true}
            isClearable={true}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 2000 }),
              menu: (base) => ({ ...base, maxHeight: 320 }),
              menuList: (base) => ({ ...base, maxHeight: 320 }),
            }}
            menuPortalTarget={document.body}
          />
        )}
        {marketStatusError && <small className="text-danger">{marketStatusError}</small>}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant={marketStatusCode !== null && isSubmitting === true ? 'success' : 'primary'}
          onClick={handleUpdateMarketStatus}
          disabled={marketStatusCode !== null && isSubmitting === true}
        >
          {marketStatusCode !== null && isSubmitting === true ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
